import React, { useRef } from "react";

import { Tooltip } from "@mui/material";
import ShipeeziGrid, { GeneralActions } from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";
import { FiClipboard } from "react-icons/fi";

import { useAppState } from "../../../../context/StateContext";
import { Booking } from "../../../../models/booking";
import { CartonProductView, CartonView } from "../../../../models/carton";
import { Searches } from "../../../../models/shipeezi.table";
import { handleGetConvertValue } from "../../../../util/converts";
import { DecimalFormatType, formatDecimalValue } from "../../../../util/formatDecimalValue";
import { getMeasurement, getWeightMeasurement } from "../../../../util/getMeasurement";
import { Button } from "../../../Form";
import { Color } from "../../../Form/Button";
import { GridQuantity } from "../../../GridQuantity";
import { useBookingVolumeAndWeight } from "../../hooks/useBookingVolumeAndWeight";
import { SearchAttribute } from "../MarkAndNumbers/types";
import EditCartonModal, { EditCartonHandler, CartonValues } from "./EditCartonModal";
import useStyles, { RowCenter } from "./styles";

interface Props {
  booking: Booking;
  cartons: CartonView[];
  updateCartons?: (cartons: CartonView[]) => void;
  loading: boolean;
  viewOnly: boolean;
  openedFromView?: boolean;
}

const CbmWeight = (props: Props) => {
  const { booking, cartons, loading, viewOnly, updateCartons, openedFromView } = props;
  const {
    filteredCartons,
    columns,
    setSearchAttributes,
    setPage,
    page,
    pageSize,
    setPageSize,
    totalItems,
  } = useBookingVolumeAndWeight({ cartons });
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const { isFMVersion } = useAppState();
  const measurement = getMeasurement();
  const measurementWeight = getWeightMeasurement();

  const cartonRef = useRef<EditCartonHandler>(null);

  const handleInsertTotalValue = () => {
    const { cartons } = booking;

    cartonRef.current?.open(
      {
        cbm: booking.bookedMeasurement || 0,
        weight: booking.bookedGrossWeight || 0,
        quantity: booking.bookedTotalQuantity || 0,
      },
      {
        cbm: Number(
          formatDecimalValue(
            DecimalFormatType.MAERSK,
            cartons?.reduce((prev, curr) => prev + Number(curr?.measurement ?? 0), 0)
          )
        ),
        weight: Number(
          formatDecimalValue(
            DecimalFormatType.MAERSK,
            cartons?.reduce((prev, curr) => prev + Number(curr?.grossWeight ?? 0), 0)
          )
        ),
        quantity: booking.totalQuantity || 0,
      }
    );
  };

  const renderInsertTotalValue = () => {
    const isAllSSCCsReceived = booking.cartons?.length === cartons.length;

    return (
      <Tooltip title={!isAllSSCCsReceived ? translate("allSSCCsMustBeReceived") : ""}>
        <div>
          <Button
            fullWidth
            startIcon={<FiClipboard fontSize="medium" />}
            customColor={Color.SECONDARY}
            onClick={handleInsertTotalValue}
            disabled={viewOnly || !isAllSSCCsReceived}
          >
            <span className={classes.noWrap}>{translate("insertTotalValue")}</span>
          </Button>
        </div>
      </Tooltip>
    );
  };

  const handleSearch = (searches: Searches) => {
    const attributes: SearchAttribute[] = [];

    if (searches) {
      searches.forEach((search) => {
        Object.getOwnPropertyNames(search).forEach((attribute) => {
          attributes.push({
            attribute,
            values: [search[attribute]],
          });
        });
      });
    }

    setSearchAttributes(attributes);
  };

  const renderQuantities = () => {
    const bookedCBM = isFMVersion ? (
      <GridQuantity
        small={!!openedFromView}
        title={`${translate("bookedVolume")} (${(measurement?.code || "")?.toUpperCase()})`}
        value={handleGetConvertValue(
          booking?.bookedMeasurement || 0,
          measurement?.code,
          DecimalFormatType.MAERSK
        )}
        valueProps={{ "data-cy": "cbm" }}
      />
    ) : null;

    const bookedWeight = isFMVersion ? (
      <GridQuantity
        small={!!openedFromView}
        title={`${translate("bookedWeight")} (${(measurementWeight?.code || "")?.toUpperCase()})`}
        value={handleGetConvertValue(
          booking?.bookedGrossWeight || 0,
          measurementWeight?.code,
          DecimalFormatType.MAERSK
        )}
        valueProps={{ "data-cy": "weight" }}
      />
    ) : null;

    const items = [bookedCBM, bookedWeight];

    return (
      <RowCenter>
        {items.map((item, index) => (
          <React.Fragment key={index}>{item}</React.Fragment>
        ))}
      </RowCenter>
    );
  };

  const generalActions: GeneralActions = {
    orderBy: {
      right: !viewOnly ? ["handleInsertTotalValue"] : ["handleQuantities"],
      left: !viewOnly ? ["handleSearch", "handleQuantities"] : undefined,
    },
    handleInsertTotalValue: renderInsertTotalValue(),
    handleSearch: {
      onSearch: handleSearch,
    },
    handleQuantities: renderQuantities(),
  };

  const renderEditCartonModal = () => {
    const editTotalValue = (cartonValues: CartonValues) => {
      const sumProducts = (count: number, product: CartonProductView) =>
        count + (product?.quantityInCarton || 0);
      const sumCartons = (count: number, carton: CartonView) =>
        count + (carton?.products?.reduce(sumProducts, 0) || 0);

      const productsQuantity = cartons.reduce(sumCartons, 0);

      if (productsQuantity === 0) return;

      const dividedCbm = cartonValues.cbm / productsQuantity;

      const dividedWeight = cartonValues.weight / productsQuantity;

      const updatedCartons = cartons.map((carton) => {
        let updatedProducts: CartonProductView[] = carton.products || [];
        let productsQuantityByCarton = 0;

        if (carton?.products) {
          productsQuantityByCarton = carton?.products.reduce(sumProducts, 0);
          updatedProducts = carton.products.map((product) => {
            return {
              ...product,
              cfsCbm: dividedCbm * (product?.quantityInCarton || 0),
              cfsWeight: dividedWeight * (product?.quantityInCarton || 0),
            };
          });
        }

        return {
          ...carton,
          measurement: dividedCbm * productsQuantityByCarton,
          grossWeight: dividedWeight * productsQuantityByCarton,
          products: [...updatedProducts],
        };
      });

      updateCartons?.(updatedCartons);
    };

    return <EditCartonModal ref={cartonRef} onEditTotalValue={editTotalValue} />;
  };

  return (
    <div className={classes.mainDiv}>
      {renderEditCartonModal()}

      <div className={classes.grid}>
        <ShipeeziGrid
          data={filteredCartons}
          totalItems={totalItems}
          columns={columns}
          loadingData={loading}
          emptyData={translate("noInformationToBeShown")}
          pageSize={pageSize}
          currentPage={page}
          pageSizeList={[10, 30, 50, 100]}
          positionActionsColumn="right"
          onChangePageSize={setPageSize}
          onNextPage={setPage}
          onPreviousPage={setPage}
          onGoToPage={setPage}
          generalActions={generalActions}
          showSelection={!viewOnly}
          showColumnChooser={false}
        />
      </div>
    </div>
  );
};

export default CbmWeight;
